import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PrivateRoute from './components/common/PrivateRoute';
import TLOnlyRoute from './components/common/TLOnlyRoute';
import ResumeMarketingAssignedCandidateEditRoute from './components/common/ResumeMarketingAssignedCandidateEditRoute';
import ResumeMarketingResumeTrackingRoute from './components/common/ResumeMarketingResumeTrackingRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import { PEOPLE_DESK_LOGO } from './assets/brandAssets';

const LoginPage = lazy(() => import('./pages/auth/login/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/forgotPassword/ForgotPasswordPage'));
const SupportPage = lazy(() => import('./pages/public/support/SupportPage'));
const DepartmentEmployeeDashboard = lazy(() => import('./pages/employee/dashboard/DepartmentEmployeeDashboard'));
const EmployeeProfile = lazy(() => import('./pages/employee/profile/EmployeeProfile'));
const EmployeeAttendance = lazy(() => import('./pages/employee/attendance/EmployeeAttendance'));
const EmployeeLeave = lazy(() => import('./pages/employee/leave/EmployeeLeave'));
const EmployeePayroll = lazy(() => import('./pages/employee/payroll/EmployeePayroll'));
const EmployeeTasks = lazy(() => import('./pages/employee/tasks/EmployeeTasks'));
const EmployeePerformance = lazy(() => import('./pages/employee/performance/EmployeePerformance'));
const EmployeeLearning = lazy(() => import('./pages/employee/learning/EmployeeLearning'));
const EmployeeAssets = lazy(() => import('./pages/employee/assets/EmployeeAssets'));
const PrivacySettings = lazy(() => import('./pages/employee/privacy/PrivacySettings'));
const HRDashboard = lazy(() => import('./departments/hr/pages/dashboard/HRDashboard'));
const EmployeeManagement = lazy(() => import('./departments/hr/pages/employeeManagement/EmployeeManagement'));
const HRPayslipGeneration = lazy(() => import('./departments/hr/pages/payroll/HRPayslipGeneration'));
const SalaryManagement = lazy(() => import('./departments/hr/pages/salaryManagement/SalaryManagement'));
const ProbationTracking = lazy(() => import('./departments/hr/pages/probationTracking/ProbationTracking'));
const WelcomeKitAutomation = lazy(() => import('./departments/hr/pages/welcomeKit/WelcomeKitAutomation'));
const LockerManagement = lazy(() => import('./departments/hr/pages/lockerManagement/LockerManagement'));
const AttendanceMonitoring = lazy(() => import('./departments/hr/pages/attendanceMonitoring/AttendanceMonitoring'));
const Policies = lazy(() => import('./departments/hr/pages/policies/Policies'));
const PolicyView = lazy(() => import('./departments/hr/pages/policies/PolicyView'));
const Onboarding = lazy(() => import('./departments/hr/pages/onboarding/Onboarding'));
const OnboardingDetails = lazy(() => import('./departments/hr/pages/onboarding/OnboardingDetails'));
const ExitManagement = lazy(() => import('./departments/hr/pages/exitManagement/ExitManagement'));
const InitiateExit = lazy(() => import('./departments/hr/pages/exitManagement/InitiateExit'));
const ExitDetails = lazy(() => import('./departments/hr/pages/exitManagement/ExitDetails'));
const Compliance = lazy(() => import('./departments/hr/pages/compliance/Compliance'));
const AddCompliance = lazy(() => import('./departments/hr/pages/compliance/AddCompliance'));
const ComplianceDetails = lazy(() => import('./departments/hr/pages/compliance/ComplianceDetails'));
const Reports = lazy(() => import('./departments/hr/pages/reports/Reports'));
const HRLeaveApprovals = lazy(() => import('./departments/hr/pages/leaveApprovals/HRLeaveApprovals'));
const ReportsView = lazy(() => import('./pages/shared/ReportsView'));
const ReportDetails = lazy(() => import('./pages/shared/ReportDetails'));
const ManagerDashboard = lazy(() => import('./pages/manager/dashboard/ManagerDashboard'));
const TeamDashboard = lazy(() => import('./pages/manager/team/TeamDashboard'));
const Approvals = lazy(() => import('./pages/manager/approvals/Approvals'));
const PerformanceReviews = lazy(() => import('./pages/manager/performance/PerformanceReviews'));
const TaskManagement = lazy(() => import('./pages/manager/tasks/TaskManagement'));
const TLDashboard = lazy(() => import('./pages/tl/dashboard/TLDashboard'));
const DepartmentTLDashboard = lazy(() => import('./pages/tl/dashboard/DepartmentTLDashboard'));
const TLTeam = lazy(() => import('./pages/tl/team/Team'));
const TeamAttendance = lazy(() => import('./pages/tl/attendance/TeamAttendance'));
const TLTaskManagement = lazy(() => import('./pages/tl/tasks/TaskManagement'));
const TLApprovals = lazy(() => import('./pages/tl/approvals/Approvals'));
const TLPerformanceFeedback = lazy(() => import('./pages/tl/performance/PerformanceFeedback'));
const TLEscalations = lazy(() => import('./pages/tl/escalations/Escalations'));
const TLProductivityDashboard = lazy(() => import('./pages/tl/productivity/ProductivityDashboard'));
const TLReportGeneration = lazy(() => import('./pages/tl/reports/ReportGeneration'));
const FinanceDashboard = lazy(() => import('./departments/finance/pages/dashboard/FinanceDashboard'));
const PayrollProcessing = lazy(() => import('./departments/finance/pages/payroll/PayrollProcessing'));
const Reimbursements = lazy(() => import('./departments/finance/pages/reimbursements/Reimbursements'));
const TaxReports = lazy(() => import('./departments/finance/pages/taxReports/TaxReports'));
const Budgeting = lazy(() => import('./departments/finance/pages/budgeting/Budgeting'));
const AdminDashboard = lazy(() => import('./pages/admin/dashboard/AdminDashboard'));
const AdminEmployeeManagement = lazy(() => import('./pages/admin/employeeManagement/AdminEmployeeManagement'));
const SuperAdminDashboard = lazy(() => import('./pages/superAdmin/dashboard/SuperAdminDashboard'));
const SystemSettings = lazy(() => import('./pages/superAdmin/systemSettings/SystemSettings'));
const RoleManagement = lazy(() => import('./pages/superAdmin/roles/RoleManagement'));
const PermissionManagement = lazy(() => import('./pages/superAdmin/permissions/PermissionManagement'));
const Integrations = lazy(() => import('./pages/superAdmin/integrations/Integrations'));
const AuditLogs = lazy(() => import('./pages/superAdmin/auditLogs/AuditLogs'));
const SuperAdminEmployeeManagement = lazy(() => import('./pages/superAdmin/employeeManagement/SuperAdminEmployeeManagement'));
const DepartmentManagement = lazy(() => import('./pages/superAdmin/departments/DepartmentManagement'));
const TalentDashboard = lazy(() => import('./departments/talent/pages/dashboard/TalentDashboard'));
const CandidateReport = lazy(() => import('./departments/talent/pages/candidates/CandidateReport'));
const AddCandidate = lazy(() => import('./departments/talent/pages/candidates/AddCandidate'));
const DailyRecords = lazy(() => import('./departments/talent/pages/dailyRecords/DailyRecords'));
const InterviewList = lazy(() => import('./departments/talent/pages/interviews/InterviewList'));
const DevelopmentDashboard = lazy(() => import('./departments/development/pages/dashboard/DevelopmentDashboard'));
const DevelopmentTLDashboard = lazy(() => import('./departments/development/pages/dashboard/DevelopmentTLDashboard'));
const Projects = lazy(() => import('./departments/development/pages/projects/Projects'));
const AddProject = lazy(() => import('./departments/development/pages/projects/AddProject'));
const ProjectDetail = lazy(() => import('./departments/development/pages/projects/ProjectDetail'));
const Sprints = lazy(() => import('./departments/development/pages/sprints/Sprints'));
const AddSprint = lazy(() => import('./departments/development/pages/sprints/AddSprint'));
const EditSprint = lazy(() => import('./departments/development/pages/sprints/EditSprint'));
const SprintDetail = lazy(() => import('./departments/development/pages/sprints/SprintDetail'));
const Bugs = lazy(() => import('./departments/development/pages/bugs/Bugs'));
const AddBug = lazy(() => import('./departments/development/pages/bugs/AddBug'));
const CodeReviews = lazy(() => import('./departments/development/pages/codeReviews/CodeReviews'));
const AddCodeReview = lazy(() => import('./departments/development/pages/codeReviews/AddCodeReview'));
const Deployments = lazy(() => import('./departments/development/pages/deployments/Deployments'));
const AddDeployment = lazy(() => import('./departments/development/pages/deployments/AddDeployment'));
const ResumeMarketingDashboard = lazy(() => import('./departments/resumeMarketing/pages/dashboard/ResumeMarketingDashboard'));
const ClientSubmissions = lazy(() => import('./departments/resumeMarketing/pages/clientSubmissions/ClientSubmissions'));
const AddClientSubmission = lazy(() => import('./departments/resumeMarketing/pages/clientSubmissions/AddClientSubmission'));
const AssignedCandidates = lazy(() => import('./departments/resumeMarketing/pages/assignedCandidates/AssignedCandidates'));
const AddAssignedCandidate = lazy(() => import('./departments/resumeMarketing/pages/assignedCandidates/AddAssignedCandidate'));
const MarketingCandidateProfile = lazy(() => import('./departments/resumeMarketing/pages/candidates/MarketingCandidateProfile'));
const InterviewTracking = lazy(() => import('./departments/resumeMarketing/pages/interviewTracking/InterviewTracking'));
const AddInterviewTracking = lazy(() => import('./departments/resumeMarketing/pages/interviewTracking/AddInterviewTracking'));
const ResumeTracking = lazy(() => import('./departments/resumeMarketing/pages/resumeTracking/ResumeTracking'));
const AddResumeTracking = lazy(() => import('./departments/resumeMarketing/pages/resumeTracking/AddResumeTracking'));
const MonthlyPerformance = lazy(() => import('./departments/resumeMarketing/pages/monthlyPerformance/MonthlyPerformance'));
const DailySubmissions = lazy(() => import('./departments/resumeMarketing/pages/dailySubmissions/DailySubmissions'));
const AtsChecker = lazy(() => import('./departments/resumeMarketing/pages/atsChecker/AtsChecker'));
const DigitalMarketingDashboard = lazy(() => import('./departments/digitalMarketing/pages/dashboard/DigitalMarketingDashboard'));
const CampaignCalendar = lazy(() => import('./departments/digitalMarketing/pages/campaignCalendar/CampaignCalendar'));
const AddCampaignCalendarEvent = lazy(() => import('./departments/digitalMarketing/pages/campaignCalendar/AddCampaignCalendarEvent'));
const LeadTracking = lazy(() => import('./departments/digitalMarketing/pages/leadTracking/LeadTracking'));
const AddLead = lazy(() => import('./departments/digitalMarketing/pages/leadTracking/AddLead'));
const LeadDetail = lazy(() => import('./departments/digitalMarketing/pages/leadTracking/LeadDetail'));
const SocialMediaPlanner = lazy(() => import('./departments/digitalMarketing/pages/socialMediaPlanner/SocialMediaPlanner'));
const AddSocialMediaPost = lazy(() => import('./departments/digitalMarketing/pages/socialMediaPlanner/AddSocialMediaPost'));
const SocialMediaPostDetail = lazy(() => import('./departments/digitalMarketing/pages/socialMediaPlanner/SocialMediaPostDetail'));
const SEOAnalytics = lazy(() => import('./departments/digitalMarketing/pages/seoAnalytics/SEOAnalytics'));
const AddSEOAnalytics = lazy(() => import('./departments/digitalMarketing/pages/seoAnalytics/AddSEOAnalytics'));
const SEOAnalyticsDetail = lazy(() => import('./departments/digitalMarketing/pages/seoAnalytics/SEOAnalyticsDetail'));
const CreativeTasks = lazy(() => import('./departments/digitalMarketing/pages/creativeTasks/CreativeTasks'));
const AddCreativeTask = lazy(() => import('./departments/digitalMarketing/pages/creativeTasks/AddCreativeTask'));
const CreativeTaskDetail = lazy(() => import('./departments/digitalMarketing/pages/creativeTasks/CreativeTaskDetail'));
const TechnicalSupportDashboard = lazy(() => import('./departments/technicalSupport/pages/dashboard/TechnicalSupportDashboard'));
const TicketManagement = lazy(() => import('./departments/technicalSupport/pages/tickets/TicketManagement'));
const AddTicket = lazy(() => import('./departments/technicalSupport/pages/tickets/AddTicket'));
const TicketDetail = lazy(() => import('./departments/technicalSupport/pages/tickets/TicketDetail'));
const SLATracking = lazy(() => import('./departments/technicalSupport/pages/sla/SLATracking'));
const KnowledgeBase = lazy(() => import('./departments/technicalSupport/pages/knowledgeBase/KnowledgeBase'));
const AddKnowledgeBaseArticle = lazy(() => import('./departments/technicalSupport/pages/knowledgeBase/AddKnowledgeBaseArticle'));
const KnowledgeBaseArticleDetail = lazy(() => import('./departments/technicalSupport/pages/knowledgeBase/KnowledgeBaseArticleDetail'));
const CustomerIssueHistory = lazy(() => import('./departments/technicalSupport/pages/customerHistory/CustomerIssueHistory'));
const ResolutionAnalytics = lazy(() => import('./departments/technicalSupport/pages/analytics/ResolutionAnalytics'));
const EscalationManagement = lazy(() => import('./departments/technicalSupport/pages/escalation/EscalationManagement'));

const PRELOADERS_BY_ROLE = {
  Employee: [
    () => import('./pages/employee/attendance/EmployeeAttendance'),
    () => import('./pages/employee/leave/EmployeeLeave'),
    () => import('./pages/employee/payroll/EmployeePayroll'),
    () => import('./pages/employee/tasks/EmployeeTasks'),
  ],
  TL: [
    () => import('./pages/employee/attendance/EmployeeAttendance'),
    () => import('./pages/tl/dashboard/DepartmentTLDashboard'),
    () => import('./departments/talent/pages/dashboard/TalentDashboard'),
    () => import('./departments/resumeMarketing/pages/dashboard/ResumeMarketingDashboard'),
    () => import('./pages/tl/team/Team'),
    () => import('./pages/tl/approvals/Approvals'),
  ],
  HR: [
    () => import('./departments/hr/pages/dashboard/HRDashboard'),
    () => import('./departments/hr/pages/employeeManagement/EmployeeManagement'),
    () => import('./departments/hr/pages/payroll/HRPayslipGeneration'),
    () => import('./departments/hr/pages/salaryManagement/SalaryManagement'),
    () => import('./departments/hr/pages/lockerManagement/LockerManagement'),
    () => import('./departments/hr/pages/attendanceMonitoring/AttendanceMonitoring'),
    () => import('./departments/hr/pages/leaveApprovals/HRLeaveApprovals'),
  ],
  Manager: [
    () => import('./pages/manager/dashboard/ManagerDashboard'),
    () => import('./pages/manager/team/TeamDashboard'),
    () => import('./pages/manager/approvals/Approvals'),
  ],
  Finance: [
    () => import('./departments/finance/pages/dashboard/FinanceDashboard'),
    () => import('./departments/finance/pages/payroll/PayrollProcessing'),
    () => import('./departments/finance/pages/reimbursements/Reimbursements'),
  ],
  Admin: [
    () => import('./pages/admin/dashboard/AdminDashboard'),
    () => import('./pages/admin/employeeManagement/AdminEmployeeManagement'),
    () => import('./pages/shared/ReportsView'),
  ],
  'Super Admin': [
    () => import('./pages/superAdmin/dashboard/SuperAdminDashboard'),
    () => import('./pages/superAdmin/employeeManagement/SuperAdminEmployeeManagement'),
    () => import('./pages/superAdmin/systemSettings/SystemSettings'),
  ],
};

const scheduleRoutePreload = (loaders) => {
  if (!Array.isArray(loaders) || !loaders.length) return;
  const run = () => {
    loaders.forEach((load) => {
      Promise.resolve()
        .then(() => load())
        .catch(() => {
          /* ignore preload failures */
        });
    });
  };

  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 800);
  }
};

function App() {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  useEffect(() => {
    document.title = 'People Desk Portal';
    let favicon = document.querySelector("link[rel='icon']");
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.setAttribute('rel', 'icon');
      document.head.appendChild(favicon);
    }
    favicon.setAttribute('type', 'image/png');
    favicon.setAttribute('href', PEOPLE_DESK_LOGO);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const roleName = user?.role?.name;
    if (!roleName) return;
    scheduleRoutePreload(PRELOADERS_BY_ROLE[roleName]);
  }, [isAuthenticated, user?.role?.name]);

  return (
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-sm text-content-muted">Loading...</div>}>
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LoginPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/support" element={<SupportPage />} />

      {/* Protected Routes */}
      <Route
        element={
          <PrivateRoute>
            <DashboardLayout />
          </PrivateRoute>
        }
      >
        {/* Employee Routes */}
        <Route path="/employee/dashboard" element={<DepartmentEmployeeDashboard />} />
        <Route path="/employee/profile" element={<EmployeeProfile />} />
        <Route path="/employee/attendance" element={<EmployeeAttendance />} />
        <Route path="/employee/leave" element={<EmployeeLeave />} />
        <Route path="/employee/payroll" element={<EmployeePayroll />} />
        <Route path="/employee/tasks" element={<EmployeeTasks />} />
        <Route path="/employee/performance" element={<EmployeePerformance />} />
        <Route path="/employee/learning" element={<EmployeeLearning />} />
        <Route path="/employee/assets" element={<EmployeeAssets />} />
        <Route path="/employee/privacy" element={<PrivacySettings />} />

        {/* HR Routes */}
        <Route path="/hr/dashboard" element={<HRDashboard />} />
        <Route path="/hr/employees" element={<EmployeeManagement />} />
        <Route path="/hr/payroll" element={<HRPayslipGeneration />} />
        <Route path="/hr/salary-management" element={<SalaryManagement />} />
        <Route path="/hr/probation-tracking" element={<ProbationTracking />} />
        <Route path="/hr/welcome-kit" element={<WelcomeKitAutomation />} />
        <Route path="/hr/locker-management" element={<LockerManagement />} />
        <Route path="/hr/attendance-monitoring" element={<AttendanceMonitoring />} />
        <Route path="/hr/policies" element={<Policies />} />
        <Route path="/hr/policies/:id" element={<PolicyView />} />
        <Route path="/hr/onboarding" element={<Onboarding />} />
        <Route path="/hr/onboarding/:id" element={<OnboardingDetails />} />
        <Route path="/hr/exits" element={<ExitManagement />} />
        <Route path="/hr/exits/new" element={<InitiateExit />} />
        <Route path="/hr/exits/:id" element={<ExitDetails />} />
        <Route path="/hr/compliance" element={<Compliance />} />
        <Route path="/hr/compliance/new" element={<AddCompliance />} />
        <Route path="/hr/compliance/:id" element={<ComplianceDetails />} />
        <Route path="/hr/reports" element={<Reports />} />
        <Route path="/hr/reports/:id" element={<ReportDetails basePath="/hr/reports" />} />
        <Route path="/hr/approvals/leaves" element={<HRLeaveApprovals />} />

        {/* Manager Routes */}
        <Route path="/manager/dashboard" element={<ManagerDashboard />} />
        <Route path="/manager/team" element={<TeamDashboard />} />
        <Route path="/manager/approvals" element={<Approvals />} />
        <Route path="/manager/performance-reviews" element={<PerformanceReviews />} />
        <Route path="/manager/tasks" element={<TaskManagement />} />

        {/* TL Routes */}
        <Route path="/tl/dashboard" element={<DepartmentTLDashboard />} />
        <Route path="/tl/team" element={<TLTeam />} />
        <Route path="/tl/attendance" element={<TeamAttendance />} />
        <Route path="/tl/tasks" element={<TLTaskManagement />} />
        <Route path="/tl/approvals" element={<TLApprovals />} />
        <Route path="/tl/performance" element={<TLPerformanceFeedback />} />
        <Route path="/tl/escalations" element={<TLEscalations />} />
        <Route path="/tl/productivity" element={<TLProductivityDashboard />} />
        <Route path="/tl/reports" element={<TLReportGeneration />} />

        {/* Finance Routes */}
        <Route path="/finance/dashboard" element={<FinanceDashboard />} />
        <Route path="/finance/payroll" element={<PayrollProcessing />} />
        <Route path="/finance/reimbursements" element={<Reimbursements />} />
        <Route path="/finance/tax-reports" element={<TaxReports />} />
        <Route path="/finance/budgeting" element={<Budgeting />} />

        {/* Admin Routes */}
        <Route path="/admin/dashboard" element={<AdminDashboard />} />
        <Route path="/admin/employees" element={<AdminEmployeeManagement />} />
        <Route path="/admin/reports" element={<ReportsView canGenerate={true} basePath="/admin/reports" />} />
        <Route path="/admin/reports/:id" element={<ReportDetails basePath="/admin/reports" />} />

        {/* Super Admin Routes */}
        <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
        <Route path="/super-admin/system-settings" element={<SystemSettings />} />
        <Route path="/super-admin/roles" element={<RoleManagement />} />
        <Route path="/super-admin/permissions" element={<PermissionManagement />} />
        <Route path="/super-admin/integrations" element={<Integrations />} />
        <Route path="/super-admin/audit-logs" element={<AuditLogs />} />
        <Route path="/super-admin/employees" element={<SuperAdminEmployeeManagement />} />
        <Route path="/super-admin/departments" element={<DepartmentManagement />} />
        <Route path="/super-admin/reports" element={<ReportsView canGenerate={true} basePath="/super-admin/reports" />} />
        <Route path="/super-admin/reports/:id" element={<ReportDetails basePath="/super-admin/reports" />} />

        {/* Department Routes */}
        <Route path="/talent/dashboard" element={<TalentDashboard />} />
        <Route path="/talent/candidates" element={<CandidateReport />} />
        <Route path="/talent/candidates/new" element={<AddCandidate />} />
        <Route path="/talent/candidates/:id" element={<AddCandidate />} />
        <Route path="/talent/candidates/:id/edit" element={<AddCandidate />} />
        <Route path="/talent/interviews" element={<InterviewList />} />
        <Route path="/talent/daily-records" element={<DailyRecords />} />
        <Route path="/resume-marketing/dashboard" element={<ResumeMarketingDashboard />} />
        <Route path="/resume-marketing/active-candidates" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/resume-marketing/active-candidates/new" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/resume-marketing/active-candidates/:id" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/resume-marketing/active-candidates/:id/edit" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/resume-marketing/candidates/:id" element={<MarketingCandidateProfile />} />
        <Route path="/resume-marketing/client-submissions" element={<ClientSubmissions />} />
        <Route path="/resume-marketing/client-submissions/new" element={<AddClientSubmission />} />
        <Route path="/resume-marketing/client-submissions/:id" element={<AddClientSubmission />} />
        <Route path="/resume-marketing/assigned-candidates" element={<AssignedCandidates />} />
        <Route path="/resume-marketing/assigned-candidates/new" element={<TLOnlyRoute><AddAssignedCandidate /></TLOnlyRoute>} />
        <Route
          path="/resume-marketing/assigned-candidates/:id/edit"
          element={
            <ResumeMarketingAssignedCandidateEditRoute>
              <AddAssignedCandidate />
            </ResumeMarketingAssignedCandidateEditRoute>
          }
        />
        <Route path="/resume-marketing/interview-tracking" element={<InterviewTracking />} />
        <Route path="/resume-marketing/interview-tracking/new" element={<AddInterviewTracking />} />
        <Route path="/resume-marketing/interview-tracking/:id" element={<AddInterviewTracking />} />
        <Route
          path="/resume-marketing/resume-tracking"
          element={
            <ResumeMarketingResumeTrackingRoute>
              <ResumeTracking />
            </ResumeMarketingResumeTrackingRoute>
          }
        />
        <Route
          path="/resume-marketing/resume-tracking/new"
          element={
            <ResumeMarketingResumeTrackingRoute requireCreate>
              <AddResumeTracking />
            </ResumeMarketingResumeTrackingRoute>
          }
        />
        <Route
          path="/resume-marketing/resume-tracking/:id"
          element={
            <ResumeMarketingResumeTrackingRoute>
              <AddResumeTracking />
            </ResumeMarketingResumeTrackingRoute>
          }
        />
        <Route path="/resume-marketing/monthly-performance" element={<MonthlyPerformance />} />
        <Route path="/resume-marketing/daily-submissions" element={<DailySubmissions />} />
        <Route path="/resume-marketing/ats-checker" element={<AtsChecker />} />
        <Route path="/development/dashboard" element={<DevelopmentDashboard />} />
        <Route path="/development/tl-dashboard" element={<TLOnlyRoute><DevelopmentTLDashboard /></TLOnlyRoute>} />
        <Route path="/development/projects" element={<Projects />} />
        <Route path="/development/projects/new" element={<TLOnlyRoute><AddProject /></TLOnlyRoute>} />
        <Route path="/development/projects/:id/edit" element={<TLOnlyRoute><AddProject /></TLOnlyRoute>} />
        <Route path="/development/projects/:id" element={<ProjectDetail />} />
        <Route path="/development/sprints" element={<Sprints />} />
        <Route path="/development/sprints/new" element={<TLOnlyRoute><AddSprint /></TLOnlyRoute>} />
        <Route
          path="/development/sprints/:id/edit"
          element={
            <TLOnlyRoute>
              <EditSprint />
            </TLOnlyRoute>
          }
        />
        <Route path="/development/sprints/:id" element={<SprintDetail />} />
        <Route path="/development/bugs" element={<Bugs />} />
        <Route path="/development/bugs/new" element={<AddBug />} />
        <Route path="/development/code-reviews" element={<CodeReviews />} />
        <Route path="/development/code-reviews/new" element={<AddCodeReview />} />
        <Route path="/development/deployments" element={<Deployments />} />
        <Route path="/development/deployments/new" element={<AddDeployment />} />
        <Route path="/digital-marketing/dashboard" element={<DigitalMarketingDashboard />} />
        <Route path="/digital-marketing/campaign-calendar" element={<CampaignCalendar />} />
        <Route path="/digital-marketing/campaign-calendar/new" element={<AddCampaignCalendarEvent />} />
        <Route path="/digital-marketing/campaign-calendar/:id/edit" element={<AddCampaignCalendarEvent />} />
        <Route path="/digital-marketing/leads" element={<LeadTracking />} />
        <Route path="/digital-marketing/leads/new" element={<AddLead />} />
        <Route path="/digital-marketing/leads/:id/edit" element={<AddLead />} />
        <Route path="/digital-marketing/leads/:id" element={<LeadDetail />} />
        <Route path="/digital-marketing/social-media-posts" element={<SocialMediaPlanner />} />
        <Route path="/digital-marketing/social-media-posts/new" element={<AddSocialMediaPost />} />
        <Route path="/digital-marketing/social-media-posts/:id/edit" element={<AddSocialMediaPost />} />
        <Route path="/digital-marketing/social-media-posts/:id" element={<SocialMediaPostDetail />} />
        <Route path="/digital-marketing/seo-analytics" element={<SEOAnalytics />} />
        <Route path="/digital-marketing/seo-analytics/new" element={<AddSEOAnalytics />} />
        <Route path="/digital-marketing/seo-analytics/:id/edit" element={<AddSEOAnalytics />} />
        <Route path="/digital-marketing/seo-analytics/:id" element={<SEOAnalyticsDetail />} />
        <Route path="/digital-marketing/creative-tasks" element={<CreativeTasks />} />
        <Route path="/digital-marketing/creative-tasks/new" element={<AddCreativeTask />} />
        <Route path="/digital-marketing/creative-tasks/:id/edit" element={<AddCreativeTask />} />
        <Route path="/digital-marketing/creative-tasks/:id" element={<CreativeTaskDetail />} />
        <Route path="/technical-support/dashboard" element={<TechnicalSupportDashboard />} />
        <Route path="/technical-support/tickets" element={<TicketManagement />} />
        <Route path="/technical-support/tickets/new" element={<AddTicket />} />
        <Route path="/technical-support/tickets/:id" element={<TicketDetail />} />
        <Route path="/technical-support/sla" element={<SLATracking />} />
        <Route path="/technical-support/knowledge-base" element={<KnowledgeBase />} />
        <Route path="/technical-support/knowledge-base/new" element={<AddKnowledgeBaseArticle />} />
        <Route path="/technical-support/knowledge-base/:id" element={<KnowledgeBaseArticleDetail />} />
        <Route path="/technical-support/customer-history" element={<CustomerIssueHistory />} />
        <Route path="/technical-support/analytics" element={<ResolutionAnalytics />} />
        <Route path="/technical-support/escalations" element={<EscalationManagement />} />

        {/* Default redirect based on role */}
        <Route
          path="/dashboard"
          element={
            <Navigate
              to={
                user?.role?.name === 'Super Admin'
                  ? '/super-admin/dashboard'
                  : user?.role?.name === 'Admin'
                  ? '/admin/dashboard'
                  : user?.role?.name === 'HR'
                  ? '/hr/dashboard'
                  : user?.role?.name === 'Finance'
                  ? '/finance/dashboard'
                  : user?.role?.name === 'Manager'
                  ? '/manager/dashboard'
                  : user?.role?.name === 'TL'
                  ? '/employee/attendance'
                  : '/employee/attendance'
              }
              replace
            />
          }
        />
      </Route>

      {/* Unknown URL: avoid sending logged-in users to login (looks like a forced logout). */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
} 
export default App;
/* th */